import { useCallback, useEffect, useState } from 'react';
import { fetchAllPages } from '../../lib/api';
import { auditsAPI, type AuditAction, type AuditSummary, type SupplierAudit } from '../../lib/api/audits';
import { getErrorMessage } from '../../hooks/useFetchData';
import { useView } from '../../contexts/ViewContext';

interface AuditsData {
  audits: SupplierAudit[];
  actions: AuditAction[];
  summary: AuditSummary | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Every audit and action the caller may see, loaded up front for in-memory
 * list controls, plus the tile counts. Re-scoped whenever the view switcher
 * changes (`viewKey`) because the endpoints narrow on `?site=` / `?group=`.
 *
 * @returns audits, actions, summary, loading/error state and a reload callback
 */
export function useAuditsData(): AuditsData {
  const { getViewQueryParams, viewKey } = useView();
  const [audits, setAudits] = useState<SupplierAudit[]>([]);
  const [actions, setActions] = useState<AuditAction[]>([]);
  const [summary, setSummary] = useState<AuditSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    const scope: Record<string, string | number | boolean> = {};
    Object.entries(getViewQueryParams()).forEach(([k, v]) => { if (v !== undefined) scope[k] = v; });
    setLoading(true);
    setError(null);
    Promise.all([
      fetchAllPages<SupplierAudit>((page) => auditsAPI.list({ ...scope, page, page_size: 500 })),
      fetchAllPages<AuditAction>((page) => auditsAPI.listActions({ ...scope, page, page_size: 500 })),
      auditsAPI.summary(scope),
    ])
      .then(([auditRows, actionRows, summaryRes]) => {
        if (cancelled) return;
        setAudits(auditRows);
        setActions(actionRows);
        setSummary(summaryRes.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Audits load failed:', err);
        setError(getErrorMessage(err, 'Could not load audits'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [getViewQueryParams, viewKey, tick]);

  return { audits, actions, summary, loading, error, reload };
}
